/**
 * useSpeechRecognition — Phase 1 voice input for the AI Assistant.
 *
 * Browser-only Web Speech API wrapper. Does NOT record audio, does NOT send
 * audio anywhere — the browser handles speech-to-text locally (or via the
 * browser vendor's own pipeline) and we only consume the resulting text
 * transcript.
 *
 * Safety boundary: this hook only produces a `transcript` string. It cannot
 * place trades, cannot enable live trading, cannot bypass credential
 * refusals. The caller is responsible for routing the transcript into a
 * normal text input field — never into an auto-submit path.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type SpeechRecognitionResultLike = {
  isFinal: boolean;
  0: { transcript: string };
};
type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
};
type SpeechRecognitionErrorEventLike = {
  error: string;
  message?: string;
};
interface SpeechRecognitionInstance {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export type SpeechRecognitionStatus = 'idle' | 'listening' | 'error';

export interface UseSpeechRecognitionResult {
  supported: boolean;
  listening: boolean;
  status: SpeechRecognitionStatus;
  transcript: string;
  interimTranscript: string;
  errorMessage: string | null;
  start: () => void;
  stop: () => void;
  reset: () => void;
}

export interface UseSpeechRecognitionOptions {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
}

export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions = {}
): UseSpeechRecognitionResult {
  const { lang = 'en-US', continuous = false, interimResults = true } = options;

  const Ctor = useMemo(getSpeechRecognitionCtor, []);
  const supported = Ctor !== null;

  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<SpeechRecognitionStatus>('idle');

  const recRef = useRef<SpeechRecognitionInstance | null>(null);

  useEffect(() => {
    return () => {
      const r = recRef.current;
      if (r) {
        try {
          r.onresult = null;
          r.onerror = null;
          r.onend = null;
          r.onstart = null;
          r.abort();
        } catch {
          // ignore
        }
      }
      recRef.current = null;
    };
  }, []);

  const start = useCallback(() => {
    if (!Ctor) {
      setErrorMessage(
        'Voice input is not supported in this browser. Please type your message instead.'
      );
      setStatus('error');
      return;
    }
    if (recRef.current) return;
    let rec: SpeechRecognitionInstance;
    try {
      rec = new Ctor();
    } catch (e) {
      setErrorMessage(
        e instanceof Error
          ? `Could not initialize voice input: ${e.message}`
          : 'Could not initialize voice input.'
      );
      setStatus('error');
      return;
    }
    rec.lang = lang;
    rec.continuous = continuous;
    rec.interimResults = interimResults;
    rec.maxAlternatives = 1;

    rec.onstart = () => {
      setListening(true);
      setStatus('listening');
      setErrorMessage(null);
    };

    rec.onresult = (event: SpeechRecognitionEventLike) => {
      let interim = '';
      let finalChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result[0]?.transcript ?? '';
        if (result.isFinal) {
          finalChunk += text;
        } else {
          interim += text;
        }
      }
      if (finalChunk) {
        setTranscript((prev) => (prev ? `${prev} ${finalChunk}`.trim() : finalChunk.trim()));
      }
      setInterimTranscript(interim);
    };

    rec.onerror = (event: SpeechRecognitionErrorEventLike) => {
      const code = event.error || 'unknown';
      const friendly =
        code === 'not-allowed' || code === 'service-not-allowed'
          ? 'Microphone permission was denied. Enable mic access in your browser settings.'
          : code === 'no-speech'
            ? 'No speech detected. Tap the mic and try again.'
            : code === 'audio-capture'
              ? 'No microphone found. Check your audio input device.'
              : code === 'aborted'
                ? null
                : `Voice input error: ${code}.`;
      if (friendly) {
        setErrorMessage(friendly);
        setStatus('error');
      }
      setListening(false);
      setInterimTranscript('');
    };

    rec.onend = () => {
      setListening(false);
      setInterimTranscript('');
      setStatus((prev) => (prev === 'error' ? 'error' : 'idle'));
      recRef.current = null;
    };

    recRef.current = rec;
    try {
      rec.start();
    } catch (e) {
      setErrorMessage(
        e instanceof Error ? `Could not start voice input: ${e.message}` : 'Could not start voice input.'
      );
      setStatus('error');
      setListening(false);
      recRef.current = null;
    }
  }, [Ctor, lang, continuous, interimResults]);

  const stop = useCallback(() => {
    const r = recRef.current;
    if (!r) return;
    try {
      r.stop();
    } catch {
      // ignore
    }
  }, []);

  const reset = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setErrorMessage(null);
    if (status === 'error') setStatus('idle');
  }, [status]);

  return {
    supported,
    listening,
    status,
    transcript,
    interimTranscript,
    errorMessage,
    start,
    stop,
    reset,
  };
}

export default useSpeechRecognition;
